import { useCallback, useMemo, useRef } from 'react'
import { query, where } from 'firebase/firestore'
import { writeEventInterest } from '../lib/interestWrites'
import { eventInterestsCol } from '../lib/converters'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { announceRsvp } from '../lib/rsvpBridge'
import { attendanceStatus, type EventAttendanceStatus, type EventInterest } from '../types'
import { fbt } from 'fbtee'

const EMPTY_STATUSES: Map<string, EventAttendanceStatus> = new Map()
const EMPTY_DOC_IDS: Map<string, string> = new Map()

export type EventInterestStatus = 'none' | EventAttendanceStatus

export function useUserEventInterests(userId: string | null) {
  const ref = userId
    ? query(eventInterestsCol(), where('userId', '==', userId))
    : null
  const { data, loading, error } = useFirestoreCollection<EventInterest>(ref)

  const writingRef = useRef(false)

  const { eventStatuses, docIds } = useMemo(() => {
    if (!userId) return { eventStatuses: EMPTY_STATUSES, docIds: EMPTY_DOC_IDS }
    const statuses = new Map<string, EventAttendanceStatus>()
    const ids = new Map<string, string>()
    for (const d of data) {
      statuses.set(d.eventId, attendanceStatus(d.status))
      ids.set(d.eventId, d.id)
    }
    return { eventStatuses: statuses, docIds: ids }
  }, [userId, data])

  const setEventInterest = useCallback(
    async (eventId: string, next: EventInterestStatus) => {
      if (!userId) return
      const current: EventInterestStatus = eventStatuses.get(eventId) ?? 'none'
      if (current === next) return
      await runMutation(fbt("Couldn't update event interest", 'Error toast when changing an event attendance status fails'), async () => {
        if (current === 'none') {
          if (writingRef.current) return
          writingRef.current = true
          try {
            await writeEventInterest(userId, eventId, null, next)
          } finally {
            writingRef.current = false
          }
        } else {
          const id = docIds.get(eventId)
          if (!id) return
          await writeEventInterest(userId, eventId, id, next)
        }
        track('event_interest_toggled', { status: next, event_id: eventId })
        // Only once the write landed: the confirmation doubles as the prompt to
        // link a group, and there's nothing to plan around a failed RSVP.
        if (next === 'going' || next === 'interested') announceRsvp({ eventId, status: next })
      })
    },
    [userId, eventStatuses, docIds],
  )

  return { eventStatuses, loading, error, setEventInterest }
}
